import { supabase, CampanaMaster } from '../lib/supabase';
import { Usuario } from '../types/auth';

interface CampanaLocalRaw {
  id: string;
  objetivo_recaudacion: number | null;
  monto_recaudado: number | null;
  lumisial_id: string;
  lumisial?: {
    id: string;
    nombre: string;
    diocesis_id: string | null;
    diocesis?: { id: string; nombre: string } | null;
    provincia_eclesiastica_id: string | null;
  } | null;
}
import { getZonalAccess } from '../utils/permissions';

export interface CampanaConAportes {
  campana_master_id: string;
  campana_master_nombre: string;
  estado: string;
  objetivo_global: number;
  entidades: EntidadAporte[];
}

export interface EntidadAporte {
  entidad_id: string;
  entidad_nombre: string;
  tipo: 'lumisial' | 'diocesis';
  monto_objetivo: number;
  monto_recaudado: number;
}

/**
 * Filtra campañas locales por jurisdicción (diócesis o provincia eclesiástica).
 */
function filtrarPorJurisdiccion(
  locales: CampanaLocalRaw[],
  filtros: { diocesisId?: string; provinciaEclesiasticaId?: string }
): CampanaLocalRaw[] {
  if (filtros.diocesisId) {
    return locales.filter(
      c => c.lumisial?.diocesis_id === filtros.diocesisId
    );
  }
  if (filtros.provinciaEclesiasticaId) {
    return locales.filter(
      c => c.lumisial?.provincia_eclesiastica_id === filtros.provinciaEclesiasticaId
    );
  }
  return locales;
}


/**
 * Agrupa campañas locales por diócesis (para admin nacional).
 */
function agruparPorDiocesis(
  filtradas: CampanaLocalRaw[]
): EntidadAporte[] {
  const diocesisMap = new Map<string, EntidadAporte>();

  for (const c of filtradas) {
    const diocesisId = c.lumisial?.diocesis_id || '';
    const diocesisNombre = c.lumisial?.diocesis?.nombre || 'Sin Diócesis';

    if (!diocesisMap.has(diocesisId)) {
      diocesisMap.set(diocesisId, {
        entidad_id: diocesisId,
        entidad_nombre: diocesisNombre,
        tipo: 'diocesis',
        monto_objetivo: 0,
        monto_recaudado: 0,
      });
    }

    const entry = diocesisMap.get(diocesisId)!;
    entry.monto_objetivo += Number(c.objetivo_recaudacion || 0);
    entry.monto_recaudado += Number(c.monto_recaudado || 0);
  }

  return Array.from(diocesisMap.values());
}

/**
 * Mapea campañas locales a entidades lumisial (para admin diocesano).
 */
function mapearALumisiales(
  filtradas: CampanaLocalRaw[]
): EntidadAporte[] {
  return filtradas.map(c => ({
    entidad_id: c.lumisial?.id || c.lumisial_id,
    entidad_nombre: c.lumisial?.nombre || '',
    tipo: 'lumisial' as const,
    monto_objetivo: Number(c.objetivo_recaudacion || 0),
    monto_recaudado: Number(c.monto_recaudado || 0),
  }));
}

/**
 * Verifica si una entidad pertenece a la jurisdicción del admin consultando la BD.
 */
async function verificarEntidadEnJurisdiccion(
  entidadOrigenId: string,
  tipoEntidad: 'lumisial' | 'diocesis',
  rolNombre: string,
  zonalAccess: { diocesisId: string | null; provinciaEclesiasticaId: string | null }
): Promise<boolean> {
  if (rolNombre === 'admin_diocesano' && tipoEntidad === 'lumisial') {
    const { data, error } = await supabase
      .from('lumisiales')
      .select('id')
      .eq('id', entidadOrigenId)
      .eq('diocesis_id', zonalAccess.diocesisId!)
      .eq('activo', true)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  }

  if (rolNombre === 'admin_nacional' && tipoEntidad === 'diocesis') {
    const { data, error } = await supabase
      .from('diocesis')
      .select('id')
      .eq('id', entidadOrigenId)
      .eq('provincia_eclesiastica_id', zonalAccess.provinciaEclesiasticaId!)
      .eq('activo', true)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  }

  if (rolNombre === 'admin_nacional' && tipoEntidad === 'lumisial') {
    const { data, error } = await supabase
      .from('lumisiales')
      .select('id')
      .eq('id', entidadOrigenId)
      .eq('provincia_eclesiastica_id', zonalAccess.provinciaEclesiasticaId!)
      .eq('activo', true)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  }

  return false;
}


export const aporteExternoService = {
  /**
   * Listar campañas master activas con montos por entidad subordinada.
   * Para admin diocesano: filtra por diocesisId, muestra lumisiales.
   * Para admin nacional: filtra por provinciaEclesiasticaId, muestra diócesis.
   */
  async getCampanasConAportes(filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
  }): Promise<CampanaConAportes[]> {
    const masters = await this.getMastersCampanasActivas(filtros);
    if (masters.length === 0) return [];

    const resultado: CampanaConAportes[] = [];

    for (const master of masters) {
      const filtradas = await this.getCampanasLocalesFiltradas(master.id, filtros);
      if (filtradas.length === 0) continue;

      const esVistaAgrupada = filtros.provinciaEclesiasticaId && !filtros.diocesisId;
      const entidades = esVistaAgrupada
        ? agruparPorDiocesis(filtradas)
        : mapearALumisiales(filtradas);

      resultado.push({
        campana_master_id: master.id,
        campana_master_nombre: master.nombre,
        estado: master.estado,
        objetivo_global: master.objetivo_global,
        entidades,
      });
    }

    return resultado;
  },

  /**
   * Obtiene campañas master activas filtradas por jurisdicción.
   */
  async getMastersCampanasActivas(filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
  }): Promise<CampanaMaster[]> {
    let query = supabase
      .from('campanas_master')
      .select('*')
      .in('estado', ['planificacion', 'en_curso']);

    if (filtros.diocesisId) {
      query = query.or(
        `diocesis_id.eq.${filtros.diocesisId},diocesis_id.is.null`
      );
    }
    if (filtros.provinciaEclesiasticaId) {
      query = query.or(
        `provincia_eclesiastica_id.eq.${filtros.provinciaEclesiasticaId},provincia_eclesiastica_id.is.null`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Obtiene campañas locales de una master filtradas por jurisdicción.
   */
  async getCampanasLocalesFiltradas(masterId: string, filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
  }): Promise<CampanaLocalRaw[]> {
    const { data, error } = await supabase
      .from('campañas')
      .select(`
        id,
        objetivo_recaudacion,
        monto_recaudado,
        lumisial_id,
        lumisial:lumisial_id(
          id, nombre, diocesis_id,
          diocesis:diocesis_id(id, nombre),
          provincia_eclesiastica_id
        )
      `)
      .eq('campana_master_id', masterId);

    if (error) throw error;
    return filtrarPorJurisdiccion((data || []) as unknown as CampanaLocalRaw[], filtros);
  },

  /**
   * Validar que la entidad origen pertenece a la jurisdicción del admin.
   * Para admin_diocesano: el lumisial debe pertenecer a su diócesis.
   * Para admin_nacional: la diócesis debe pertenecer a su provincia eclesiástica.
   * Para admin_global: siempre tiene acceso.
   */
  validarJurisdiccion(
    adminUsuario: Usuario,
    _entidadOrigenId: string,
    tipoEntidad: 'lumisial' | 'diocesis'
  ): boolean {
    const zonalAccess = getZonalAccess(adminUsuario);
    const rolNombre = adminUsuario.rol?.nombre || '';

    if (rolNombre === 'admin_global') return true;

    if (rolNombre === 'admin_diocesano') {
      return tipoEntidad === 'lumisial' && !!zonalAccess.diocesisId;
    }

    if (rolNombre === 'admin_nacional') {
      return !!zonalAccess.provinciaEclesiasticaId;
    }

    return false;
  },

  /**
   * Validar jurisdicción contra la base de datos.
   * Verifica que la entidad realmente pertenece a la jurisdicción del admin.
   */
  async validarJurisdiccionAsync(
    adminUsuario: Usuario,
    entidadOrigenId: string,
    tipoEntidad: 'lumisial' | 'diocesis'
  ): Promise<boolean> {
    if (!this.validarJurisdiccion(adminUsuario, entidadOrigenId, tipoEntidad)) {
      return false;
    }

    const zonalAccess = getZonalAccess(adminUsuario);
    const rolNombre = adminUsuario.rol?.nombre || '';

    if (rolNombre === 'admin_global') return true;

    return verificarEntidadEnJurisdiccion(entidadOrigenId, tipoEntidad, rolNombre, zonalAccess);
  },
};
